import { Formik } from 'formik'
import * as Yup from 'yup'
import React, { useState, useEffect, useCallback } from 'react'
import {
  BoxContainer,
  Button,
  Loading,
  Typography
} from '../../../../components'
import {
  Form,
  FormBody,
  FormGroupContainer
} from '../../../../components/Form/styles'
import { Container, ContainerButton } from './styles'
import { useQuery } from 'react-query'
import ecbrApi from '../../../../services/ecbrApi'
import { ConfigurationsKeys } from '../../../../constants/adminEcbrConfigurations'
import { errorResponseStatusHandler } from '../../../../utils/handlers/errorResponseStatusHandler'
import { useModal } from '../../../../hooks/useModal'
import { AlertModal } from '../../../../modals/AlertModal'
import { ModalBodyContainer } from '../../../../modals/styles'
import Input from '../../../../components/Form/Input'
import ErrorMessage from '../../../../components/Form/ErrorMessage'
import Select from '../../../../components/Form/Select'

const formSchema = Yup.object().shape({
  bankSlipDueDays: Yup.number()
    .transform((value) => (value ? parseInt(value) : value))
    .required('Campo obrigatório')
    .typeError('Valor inválido')
    .min(1, 'O número de dias deve ser maior que 0'),
  bankSlipBankCode: Yup.string()
    .transform((_, obj) => obj?.value ?? undefined)
    .required('Campo obrigatório'),
  bankSlipInstructions: Yup.string().optional(),
  bankSlipNossoNumero: Yup.string().optional(),
  bankSlipType: Yup.string()
    .transform((_, obj) => obj?.value ?? undefined)
    .required('Campo obrigatório'),
  bankSlipStatementDescriptor: Yup.string().optional()
})

const initialValues = {
  bankSlipDueDays: '',
  bankSlipBankCode: undefined,
  bankSlipInstructions: '',
  bankSlipNossoNumero: '',
  bankSlipType: undefined,
  bankSlipStatementDescriptor: ''
}

/* Código do banco: 001 (Banco do Brasil); 033 (Santander); 104 (Caixa Econômica Federal); 197 ( Banco Stone); 237 (Bradesco); 341 (Itau); 376 (JPMorgan); 745 (Citibank) */
const bankCodes = [
  { value: '001', label: 'Banco do Brasil' },
  { value: '033', label: 'Santander' },
  { value: '104', label: 'Caixa Econômica Federal' },
  { value: '197', label: 'Banco Stone' },
  { value: '237', label: 'Bradesco' },
  { value: '341', label: 'Itau' },
  { value: '376', label: 'JPMorgan' },
  { value: '745', label: 'Citibank' }
]

/* Tipo de espécie do boleto: DM (Duplicata Mercantil) e BDP (Boleto de proposta) */
const bankSlipTypes = [
  { value: 'DM', label: 'Duplicata Mercantil' },
  { value: 'BDP', label: 'Boleto de proposta' }
]

const BankSlipConfigScreen = () => {
  const [error, setError] = useState('')
  const [saveBtnText, setSaveBtnText] = useState('Salvar Alterações')
  const [alertModal, openAlertModal] = useModal(AlertModal)

  const openAlert = useCallback(
    () =>
      openAlertModal({
        title: `Dados salvos!`,
        body: (
          <ModalBodyContainer>
            <Typography type="text" color="#7D7987">
              As configurações do boleto foram salvas com sucesso.
            </Typography>
          </ModalBodyContainer>
        )
      }),
    [openAlertModal]
  )

  const {
    data: configDueDays,
    isLoading: isLoadingDueDays,
    refetch: refetchDueDays
  } = useQuery(
    'configs_bank_slip',
    async () => {
      const { data } = await ecbrApi.configs.getByKey(
        ConfigurationsKeys.BANK_SLIP_DUE_DAYS
      )
      const { value, ...rest } = data
      return {
        value: value ? parseInt(value) : value,
        ...rest
      }
    },
    {
      onSuccess: () => {
        setError('')
      },
      onError: (err) => {
        if (err.response.status === 404) return
        setError('Não foi possível carregar os dados')
      },
      retry: false,
      refetchInterval: 0,
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      refetchOnReconnect: false
    }
  )

  const {
    data: configBankCode,
    isLoading: isLoadingBankCode,
    refetch: refetchBankCode
  } = useQuery(
    'configs_bank_slip_bank_code',
    async () => {
      const { data } = await ecbrApi.configs.getByKey(
        ConfigurationsKeys.BANK_SLIP_BANK_CODE
      )
      const { value, ...rest } = data
      return {
        value: value ?? undefined,
        ...rest
      }
    },
    {
      onSuccess: () => {
        setError('')
      },
      onError: (err) => {
        if (err.response.status === 404) return
        setError('Não foi possível carregar os dados')
      },
      retry: false,
      refetchInterval: 0,
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      refetchOnReconnect: false
    }
  )

  const {
    data: configInstructions,
    isLoading: isLoadingInstructions,
    refetch: refetchInstructions
  } = useQuery(
    'configs_bank_slip_instructions',
    async () => {
      const { data } = await ecbrApi.configs.getByKey(
        ConfigurationsKeys.BANK_SLIP_INSTRUCTIONS
      )
      const { value, ...rest } = data
      return {
        value: value ?? undefined,
        ...rest
      }
    },
    {
      onSuccess: () => {
        setError('')
      },
      onError: (err) => {
        if (err.response.status === 404) return
        setError('Não foi possível carregar os dados')
      },
      retry: false,
      refetchInterval: 0,
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      refetchOnReconnect: false
    }
  )

  const {
    data: configNossoNumero,
    isLoading: isLoadingNossoNumero,
    refetch: refetchNossoNumero
  } = useQuery(
    'configs_bank_slip_nosso_numero',
    async () => {
      const { data } = await ecbrApi.configs.getByKey(
        ConfigurationsKeys.BANK_SLIP_NOSSO_NUMERO
      )
      const { value, ...rest } = data
      return {
        value: value ?? undefined,
        ...rest
      }
    },
    {
      onSuccess: () => {
        setError('')
      },
      onError: (err) => {
        if (err.response.status === 404) return
        setError('Não foi possível carregar os dados')
      },
      retry: false,
      refetchInterval: 0,
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      refetchOnReconnect: false
    }
  )

  const {
    data: configType,
    isLoading: isLoadingType,
    refetch: refetchType
  } = useQuery(
    'configs_bank_slip_type',
    async () => {
      const { data } = await ecbrApi.configs.getByKey(
        ConfigurationsKeys.BANK_SLIP_TYPE
      )
      const { value, ...rest } = data
      return {
        value: value ?? undefined,
        ...rest
      }
    },
    {
      onSuccess: () => {
        setError('')
      },
      onError: (err) => {
        if (err.response.status === 404) return
        setError('Não foi possível carregar os dados')
      },
      retry: false,
      refetchInterval: 0,
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      refetchOnReconnect: false
    }
  )

  const {
    data: configStatementDescriptor,
    isLoading: isLoadingStatementDescriptor,
    refetch: refetchStatementDescriptor
  } = useQuery(
    'configs_bank_slip_statement_descriptor',
    async () => {
      const { data } = await ecbrApi.configs.getByKey(
        ConfigurationsKeys.BANK_SLIP_STATEMENT_DESCRIPTOR
      )
      const { value, ...rest } = data
      return {
        value: value ?? undefined,
        ...rest
      }
    },
    {
      onSuccess: () => {
        setError('')
      },
      onError: (err) => {
        if (err.response.status === 404) return
        setError('Não foi possível carregar os dados')
      },
      retry: false,
      refetchInterval: 0,
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      refetchOnReconnect: false
    }
  )

  useEffect(() => {
    refetchDueDays()
    refetchBankCode()
    refetchInstructions()
    refetchNossoNumero()
    refetchType()
    refetchStatementDescriptor()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onSubmit = async (
    {
      bankSlipDueDays,
      bankSlipBankCode,
      bankSlipInstructions,
      bankSlipNossoNumero,
      bankSlipType,
      bankSlipStatementDescriptor
    },
    { setSubmitting }
  ) => {
    try {
      setSaveBtnText('Enviando...')
      setSubmitting(true)

      const formDataDueDays = {
        key: ConfigurationsKeys.BANK_SLIP_DUE_DAYS,
        value: bankSlipDueDays.toString()
      }
      if (configDueDays) {
        const { id } = configDueDays
        await ecbrApi.configs.update(id, formDataDueDays)
      } else {
        await ecbrApi.configs.create(formDataDueDays)
      }

      const formDataBankCode = {
        key: ConfigurationsKeys.BANK_SLIP_BANK_CODE,
        value: bankSlipBankCode?.value
      }
      if (configBankCode) {
        const { id } = configBankCode
        await ecbrApi.configs.update(id, formDataBankCode)
      } else {
        await ecbrApi.configs.create(formDataBankCode)
      }

      const formDataInstructions = {
        key: ConfigurationsKeys.BANK_SLIP_INSTRUCTIONS,
        value: bankSlipInstructions
      }
      if (configInstructions) {
        const { id } = configInstructions
        await ecbrApi.configs.update(id, formDataInstructions)
      } else {
        await ecbrApi.configs.create(formDataInstructions)
      }

      const formDataNossoNumero = {
        key: ConfigurationsKeys.BANK_SLIP_NOSSO_NUMERO,
        value: bankSlipNossoNumero
      }
      if (configNossoNumero) {
        const { id } = configNossoNumero
        await ecbrApi.configs.update(id, formDataNossoNumero)
      } else {
        await ecbrApi.configs.create(formDataNossoNumero)
      }

      const formDataType = {
        key: ConfigurationsKeys.BANK_SLIP_TYPE,
        value: bankSlipType.value
      }
      if (configType) {
        const { id } = configType
        await ecbrApi.configs.update(id, formDataType)
      } else {
        await ecbrApi.configs.create(formDataType)
      }

      const formDataStatementDescriptor = {
        key: ConfigurationsKeys.BANK_SLIP_STATEMENT_DESCRIPTOR,
        value: bankSlipStatementDescriptor
      }
      if (configStatementDescriptor) {
        const { id } = configStatementDescriptor
        await ecbrApi.configs.update(id, formDataStatementDescriptor)
      } else {
        await ecbrApi.configs.create(formDataStatementDescriptor)
      }

      setSubmitting(false)
      openAlert()
      refetchDueDays()
      refetchBankCode()
      refetchInstructions()
      refetchNossoNumero()
      refetchType()
      refetchStatementDescriptor()
    } catch (err) {
      console.log({ err })
      errorResponseStatusHandler(err, setError)
    } finally {
      setSubmitting(false)
      setSaveBtnText('Salvar Alterações')
    }
  }

  const isLoading =
    isLoadingDueDays ||
    isLoadingBankCode ||
    isLoadingInstructions ||
    isLoadingNossoNumero ||
    isLoadingType ||
    isLoadingStatementDescriptor

  return (
    <BoxContainer>
      {alertModal}
      {isLoading && <Loading />}
      {!isLoading && (
        <Formik
          initialValues={{
            ...initialValues,
            ...(configDueDays?.value && {
              bankSlipDueDays: configDueDays.value
            }),
            ...(configBankCode?.value && {
              bankSlipBankCode: {
                value: configBankCode.value,
                label:
                  bankCodes.find(
                    (bankCode) => bankCode.value === configBankCode.value
                  )?.label ?? ''
              }
            }),
            ...(configInstructions?.value && {
              bankSlipInstructions: configInstructions.value
            }),
            ...(configNossoNumero?.value && {
              bankSlipNossoNumero: configNossoNumero.value
            }),
            ...(configType?.value && {
              bankSlipType: {
                value: configType.value,
                label:
                  bankSlipTypes.find(
                    (bankSlipType) => bankSlipType.value === configType.value
                  )?.label ?? ''
              }
            }),
            ...(configStatementDescriptor?.value && {
              bankSlipStatementDescriptor: configStatementDescriptor.value
            })
          }}
          validationSchema={formSchema}
          onSubmit={onSubmit}
        >
          {({ isSubmitting }) => (
            <Form maxWidth="100%" margin="0">
              <FormBody type="flex" justifyContent="space-between" flexRow>
                <Container>
                  <FormGroupContainer wrap>
                    <Input
                      label="Dias para o vencimento do boleto"
                      name="bankSlipDueDays"
                      type="number"
                      placeholder="Dias para o vencimento do boleto"
                      width="max-content"
                      disabled={isSubmitting}
                    />
                    <Select
                      label="Código do banco"
                      name="bankSlipBankCode"
                      placeholder="Código do banco"
                      width="max-content"
                      isLoading={isSubmitting}
                      options={bankCodes}
                      isClearable
                    />
                    <Input
                      label="Instruções do boleto"
                      name="bankSlipInstructions"
                      placeholder="Instruções do boleto"
                      width="400px"
                      disabled={isSubmitting}
                    />
                    <Input
                      label="Nosso número"
                      name="bankSlipNossoNumero"
                      placeholder="Nosso número"
                      width="max-content"
                      disabled={isSubmitting}
                    />
                    <Select
                      label="Tipo de boleto"
                      name="bankSlipType"
                      placeholder="Tipo de boleto"
                      width="max-content"
                      isLoading={isSubmitting}
                      options={bankSlipTypes}
                      isClearable
                    />
                    <Input
                      label="Descrição do boleto"
                      name="bankSlipStatementDescriptor"
                      placeholder="Descrição do boleto"
                      width="max-content"
                      disabled={isSubmitting}
                    />
                  </FormGroupContainer>
                  <ContainerButton>
                    <Button
                      text={saveBtnText}
                      type="submit"
                      variant="primary"
                      width="180px"
                      disabled={isSubmitting}
                    />
                  </ContainerButton>
                </Container>
              </FormBody>
            </Form>
          )}
        </Formik>
      )}
      {error && <ErrorMessage>{error}</ErrorMessage>}
    </BoxContainer>
  )
}

export default BankSlipConfigScreen
